import React, { Component } from 'react';

import { cloneDeep, isEqual } from 'lodash';
import { Loader, NoResult } from '../../misc/loader-utils';

import { dateService } from '../../../services/date';
import { getClass } from '../../../utils/component-helpers';
import overviewService from './overview-service';
import { getColumns, getPacingIntervalOptions } from './utils';
import { buildQueryString, getSelectedString, formatValue } from '../../base/common/utils';

import StatTable from '../../base/megatable/stat-table';
import StatCards from '../../base/megatable/stat-cards';
import DatePicker from '../../base/date/date-picker';
import PricingModelDropdown from '../../base/dropdowns/pricingmodel-dropdown';
import PacingIntervalDropdown from '../../base/dropdowns/pacinginterval-dropdown';
import ReconSelectionDropdown from '../../base/dropdowns/reconselection-dropdown';
import PartnerDropdown from '../../base/dropdowns/partner-dropdown';
import ColumnsDropdown from '../../base/dropdowns/columns-dropdown';
import Pagination from '../../base/common/pagination';
import Modal from '../../base/modals/base-modal';

import './base-overview.scss';

class BaseOverview extends Component {
  constructor(props, statType, tableName) {
    super(props);

    this.service = overviewService;

    this.statType = statType;
    this.tableName = tableName;
    this.columns = getColumns(this.statType);

    const defaultOrderParams = {
      'merchant-overview': 'merchants_revenue',
      'merch-ed-overview': 'spend',
      'publisher-overview': 'publisher_revenue',
    };

    this.pricingModelOptions = [
      {
        label: 'EPP',
        selected: true,
      },
      {
        label: 'CPC',
        selected: true,
      },
      {
        label: 'CPA',
        selected: false,
      },
    ];

    this.defaultParams = {
      date_from: dateService.formatDate(
        dateService.getStartOf(null, 'month'), // start of this month
      ),
      date_to: dateService.formatDate(dateService.getCurrentDateTime()),
      order_by: defaultOrderParams[statType],
      direction: 'desc',
      ...(this.statType === 'merch-ed-overview' && {
        pricing_models: getSelectedString(this.pricingModelOptions),
        pacing_intervals: 'all',
        merch_ids: 'all',
        active_only: true,
        reconciled_only: false,
      }),
    };

    this.state = {
      loading: true,
      showNoResult: false,
      stats: null,
      totals: null,
      expandedRowId: null,
      budgetRowId: null,
      showTotals: true,
      pricingModelOptions: cloneDeep(this.pricingModelOptions),
      pacingIntervalOptions: getPacingIntervalOptions(),
      merchantOptions: [],
      roiReconSelections: [],
      columnOptions: this.columns,
      reconAction: false,
      guardrailLowerBound: -5,
      guardrailUpperBound: 5,
      showModal: false,
      params: { ...this.defaultParams },

      // Pagination variables
      page: 1,
      perPage: 50,
      totalItems: 0,
    };
  }

  async componentDidMount() {
    await this.getUrlParams();
    await this.getOverviewStats();
    this.queryMerchants();
  }

  async componentDidUpdate(prevProps, prevState) {
    if (
      !isEqual(prevState.params, this.state.params) ||
      prevState.perPage !== this.state.perPage ||
      prevState.page !== this.state.page
    ) {
      await this.setUrlParams();
      this.getOverviewStats();
    } else if (
      this.statType === 'merch-ed-overview' &&
      prevState.expandedRowId !== this.state.expandedRowId
    ) {
      this.setState({ stats: this.statsWithSubrows() });
    } else if (!isEqual(prevState.roiReconSelections, this.state.roiReconSelections)) {
      this.updateStatefromReconSelections();
    }
  }

  getUrlParams() {
    const urlParams = new URLSearchParams(this.props.location.search);

    if (urlParams.toString()) {
      const newPricingModels = urlParams.get('pricing_models');
      const newPacingIntervals = urlParams.get('pacing_intervals');

      const newParams = {
        ...this.state.params,
        date_from: urlParams.get('date_from') || this.defaultParams.date_from,
        date_to: urlParams.get('date_to') || this.defaultParams.date_to,
        order_by: urlParams.get('order_by') || this.defaultParams.order_by,
        direction: urlParams.get('direction') || this.defaultParams.direction,
        ...(this.statType === 'merch-ed-overview' && {
          pricing_models: newPricingModels || this.defaultParams.pricing_models,
          merch_ids: urlParams.get('merch_ids') || this.defaultParams.merch_ids,
          pacing_intervals: newPacingIntervals || this.defaultParams.pacing_intervals,
          active_only:
            urlParams.get('active_only') === null
              ? this.defaultParams.active_only
              : urlParams.get('active_only') === 'true',
          reconciled_only:
            urlParams.get('reconciled_only') === null
              ? this.defaultParams.reconciled_only
              : urlParams.get('reconciled_only') === 'true',
        }),
      };
      if (newPricingModels) this.setPricingModelsOptionsFromQuery(newPricingModels);
      if (newPacingIntervals)
        this.setOptionsFromQueryWithDefaultAll(newPacingIntervals, 'pacingIntervalOptions');

      this.setState({
        params: newParams,
      });
    } else {
      this.setUrlParams();
    }
  }

  setUrlParams() {
    this.props.history.replace({
      pathname: this.props.location.pathname,
      search: buildQueryString(this.state.params),
    });
  }

  setPricingModelsOptionsFromQuery(urlParams) {
    /*
     * Set an array of pricing models with correct selected state
     * based on pricing models in the query string.
     */
    const pricingModels = urlParams.split(',');

    const updatedOptions = this.state.pricingModelOptions.map(modelOption => ({
      ...modelOption,
      selected: pricingModels.includes(modelOption.label),
    }));

    this.setState({ pricingModelOptions: updatedOptions });
  }

  setOptionsFromQueryWithDefaultAll(urlParams, optionsType) {
    /*
     * Set an array of pricing models with correct selected state
     * based on pricing models in the query string.
     */
    const updatedOptions = this.state[optionsType].map(option => ({
      ...option,
      selected: urlParams.includes(option.value) || urlParams === 'all',
    }));
    this.setState({ [optionsType]: updatedOptions });
  }

  getOverviewStats() {
    this.setState({
      loading: true,
    });
    const params = { ...this.state.params };
    if (this.statType === 'merch-ed-overview') {
      ['merch_ids', 'pacing_intervals'].forEach(param => {
        if (params[param] === 'all') delete params[param];
      });
      params.page = this.state.page;
      params.per_page = this.state.perPage;
    }

    this.service
      .getOverviewStats(this.statType, params)
      .then(data => {
        const totalsData = data[0].data[0];
        const statsData = data[1].data;

        this.setState({
          stats: statsData,
          totals: totalsData,
          expandedRowId: null,
          loading: false,
          showNoResult: statsData.length <= 0,
          totalItems: data[2],
        });
      })
      .catch(() => {
        this.setState({
          loading: false,
          showNoResult: true,
        });
      });
  }

  updateSortParams(sortedColumn) {
    this.setState({
      loading: true,
    });

    const sortedStats = this.service.sortOverviewStats(
      this.state.stats,
      this.state.params,
      sortedColumn,
    );

    this.setState({
      params: sortedStats.params,
      stats: sortedStats.data,
      loading: false,
    });
  }

  getDateRange() {
    // Return current date objects for DatePicker
    return {
      startDate: dateService.dateFromStr(this.state.params.date_from),
      endDate: dateService.dateFromStr(this.state.params.date_to),
    };
  }

  setDateRange(startDate, endDate) {
    this.setState({
      params: {
        ...this.state.params,
        date_from: startDate,
        date_to: endDate,
      },
    });
  }

  updatePricingModelOptions(pricingModelOptions) {
    this.setState({
      pricingModelOptions,
      params: {
        ...this.state.params,
        pricing_models: getSelectedString(pricingModelOptions),
      },
    });
  }

  updatePacingIntervalOptions(pacingIntervalOptions) {
    const selected = pacingIntervalOptions.reduce(
      (acc, curr) => (curr.selected ? acc.concat([curr.value]) : acc),
      [],
    );

    this.setState({
      pacingIntervalOptions,
      params: {
        ...this.state.params,
        pacing_intervals: JSON.stringify(selected),
      },
    });
  }

  queryMerchants() {
    if (this.statType === 'merch-ed-overview') {
      this.service.getMerchants().then(merchants => {
        const merchantOptions = merchants.map(merchant => {
          return { ...merchant, label: merchant.short_name, selected: false };
        });
        this.setState({ merchantOptions }, () => {
          this.setOptionsFromQueryWithDefaultAll(this.state.params.merch_ids, 'merchantOptions');
        });
      });
    }
  }

  updateMerchantParams(merchantOptions, activeOnly, reconciledOnly) {
    const selected = merchantOptions.reduce(
      (merchs, merch) => (merch.selected ? merchs.concat([merch.merch_id]) : merchs),
      [],
    );

    this.setState({
      merchantOptions,
      params: {
        ...this.state.params,
        merch_ids: merchantOptions.length === selected.length ? 'all' : selected.join(','),
        active_only: activeOnly,
        reconciled_only: reconciledOnly,
      },
    });
  }

  updateColumns(columnOptions) {
    this.setState({ columnOptions });
  }

  resetColumns() {
    this.setState(currentState => {
      return {
        columnOptions: currentState.columnOptions.map(column => (column.selected = true)),
      };
    });
  }

  paginationUpdated(name) {
    return value => {
      this.setState({ [name]: value });
      // When changing perPage, reset page to 1 to avoid page mismatch
      if (name === 'perPage') {
        this.setState({ page: 1 });
      }
    };
  }

  statsWithSubrows() {
    const stats = cloneDeep(this.state.stats).reduce((acc, stat) => {
      if (!stat.merchant_roi_goals) return acc;
      stat.key = stat.merch_id;
      acc.push(stat);

      if (this.state.expandedRowId === stat.merch_id) {
        const goals = stat.merchant_roi_goals.map((goal, index) => {
          return { ...goal, key: `subrow-${index}` };
        });
        acc = acc.concat(goals);
      }
      return acc;
    }, []);
    return stats;
  }

  setRowId(type, rowId) {
    let newRowId = rowId;
    if (type === 'expandedRowId' && this.state.expandedRowId === rowId) {
      newRowId = null;
    }
    return () => this.setState({ [type]: newRowId });
  }

  updateBudget(merchId, value, type, budgetId, date) {
    return overviewService
      .updateBudget(merchId, value, type, budgetId, date)
      .then(() => {
        const newStats = cloneDeep(this.state.stats).map(item => {
          if (item.merch_id === merchId) item[type] = parseFloat(value);
          return item;
        });
        this.setState({
          stats: newStats,
        });
        this.props.setSnackbar(
          'Budget update saved. Please allow up to a minute for changes to be reflected.',
        );
        return Promise.resolve();
      })
      .catch(() => {
        this.props.setSnackbar('Update failed, try again.');
        return Promise.reject();
      });
  }

  updateRoiReconSelections(merchId) {
    const roiReconSelections = [...this.state.roiReconSelections];
    if (!roiReconSelections.includes(merchId)) {
      roiReconSelections.push(merchId);
    } else {
      const merchIdx = roiReconSelections.indexOf(merchId);
      roiReconSelections.splice(merchIdx, 1);
    }
    this.setState({ roiReconSelections });
  }

  clearFilters() {
    this.setState({ params: this.defaultParams }, () => {
      const { params } = this.state;

      this.setPricingModelsOptionsFromQuery(params.pricing_models);
      this.setOptionsFromQueryWithDefaultAll(params.pacing_intervals, 'pacingIntervalOptions');
      this.setOptionsFromQueryWithDefaultAll(params.merch_ids, 'merchantOptions');
    });
  }

  updateAllRoiSelections() {
    const newSelections = this.state.roiReconSelections.length
      ? []
      : this.state.stats.map(stat => stat.merch_id);
    this.setState({ roiReconSelections: newSelections });
  }

  matchOldOverview() {
    const oldOverviewParams = {
      ...this.defaultParams,
      pricing_models: 'EPP,CPC,CPA',
      pacing_intervals: 'all',
      merch_ids: 'all',
      active_only: false,
      reconciled_only: false,
    };
    this.setState({ params: oldOverviewParams }, () => {
      const { params } = this.state;

      this.setPricingModelsOptionsFromQuery(params.pricing_models);
      this.setOptionsFromQueryWithDefaultAll(params.pacing_intervals, 'pacingIntervalOptions');
      this.setOptionsFromQueryWithDefaultAll(params.merch_ids, 'merchantOptions');
    });
  }

  updateMerchRecon() {
    const {
      roiReconSelections,
      reconAction,
      guardrailLowerBound,
      guardrailUpperBound,
    } = this.state;
    this.service
      .updateMerchRecon(roiReconSelections, reconAction, guardrailLowerBound, guardrailUpperBound)
      .then(() => {
        this.props.setSnackbar(
          'ROI selection saved. Please allow up to a minute for changes to be reflected.',
        );
        this.setState({ roiReconSelections: [] });
        this.getOverviewStats();
        return Promise.resolve();
      })
      .catch(() => {
        this.props.setSnackbar('ROI selection failed to save, try again.');
        return Promise.reject();
      });
  }

  updateReconSelectionParams(reconAction, guardrailLowerBound, guardrailUpperBound) {
    this.setState({ reconAction, guardrailLowerBound, guardrailUpperBound });
    this.toggleModal();
  }

  toggleModal() {
    this.setState({ showModal: !this.state.showModal });
  }

  updateStatefromReconSelections() {
    if (this.state.stats && this.state.roiReconSelections.length) {
      const reconSelectionsMerchants = this.state.stats.filter(merch =>
        this.state.roiReconSelections.includes(merch.merch_id),
      );
      const firstLowerBound = reconSelectionsMerchants[0].guardrail_lower_bound;
      const firstUpperBound = reconSelectionsMerchants[0].guardrail_upper_bound;
      const lowerBoundsMatch =
        typeof firstLowerBound === 'number' &&
        reconSelectionsMerchants.every(merch => merch.guardrail_lower_bound === firstLowerBound);
      const upperBoundsMatch =
        typeof firstUpperBound === 'number' &&
        reconSelectionsMerchants.every(merch => merch.guardrail_upper_bound === firstUpperBound);
      const guardrailLowerBound = lowerBoundsMatch ? firstLowerBound : -5;
      const guardrailUpperBound = upperBoundsMatch ? firstUpperBound : 5;
      const someReconciled =
        typeof reconSelectionsMerchants.find(merch => merch.is_reconciled) !== 'undefined';
      this.setState({ someReconciled, guardrailLowerBound, guardrailUpperBound });
    }
  }

  getReconModalDisplayTxt() {
    const lowerBound = formatValue(
      this.state.guardrailLowerBound,
      'number',
      'guardrail_lower_bound',
    );
    const upperBound = formatValue(
      this.state.guardrailUpperBound,
      'number',
      'guardrail_upper_bound',
    );
    return (
      `Are you sure you want to ${this.state.reconAction ? 'Enable' : 'Disable'} ` +
      `ROI Reconciliation for ${this.state.roiReconSelections.length} merchant(s)` +
      `${this.state.reconAction ? ` with ${lowerBound}% to +${upperBound}% guardrails` : ''}?`
    );
  }

  render() {
    const header =
      this.statType === 'merch-ed-overview' ? (
        <div className="header">
          <div className="title">Merchant Overview</div>
          <button className="match-old-overview-btn" onClick={this.matchOldOverview.bind(this)}>
            Match Merchant Overview V1 Settings
          </button>
        </div>
      ) : null;

    const statCards =
      this.statType === 'merch-ed-overview' && this.state.totals && this.state.showTotals ? (
        <StatCards data={this.state.totals} statType={this.statType} />
      ) : null;

    const stats =
      !this.state.loading && !this.state.showNoResult ? (
        <StatTable
          stats={this.state.stats}
          columns={this.state.columnOptions}
          statType={this.statType}
          showTotals={!this.state.loading && this.state.totals}
          totals={this.state.totals}
          sortDisabledFields={[]}
          sortOptions={this.state.params}
          updateSortParams={this.updateSortParams.bind(this)}
          tableName={this.tableName}
          location={this.props.location}
          updateBudget={this.updateBudget.bind(this)}
          expandedRowId={this.state.expandedRowId}
          budgetRowId={this.state.budgetRowId}
          setRowId={this.setRowId.bind(this)}
          updateRoiReconSelections={this.updateRoiReconSelections.bind(this)}
          roiReconSelections={this.state.roiReconSelections}
        />
      ) : null;

    const pricingModelDropdown =
      this.statType === 'merch-ed-overview' ? (
        <div className="dropdown-container pricing-model">
          <PricingModelDropdown
            gaEvent={{
              category: 'New Merchant Overview',
              label: 'Pricing Model Dropdown',
            }}
            items={this.state.pricingModelOptions}
            updatePricingModelOptions={this.updatePricingModelOptions.bind(this)}
          />
        </div>
      ) : null;

    const pacingIntervalDropdown =
      this.statType === 'merch-ed-overview' ? (
        <div className="dropdown-container pacing-interval">
          <PacingIntervalDropdown
            gaEvent={{
              category: 'New Merchant Overview',
              label: 'Pacing Interval Dropdown',
            }}
            items={this.state.pacingIntervalOptions}
            updatePacingIntervalOptions={this.updatePacingIntervalOptions.bind(this)}
          />
        </div>
      ) : null;

    const reconSelectionDropdown =
      this.statType === 'merch-ed-overview' && this.state.roiReconSelections.length ? (
        <ReconSelectionDropdown
          gaEvent={{
            category: 'New Merchant Overview',
            label: 'Recon Selection Dropdown',
          }}
          items={[]}
          someReconciled={this.state.someReconciled}
          updateReconSelectionParams={this.updateReconSelectionParams.bind(this)}
          guardrailLowerBound={this.state.guardrailLowerBound}
          guardrailUpperBound={this.state.guardrailUpperBound}
        />
      ) : null;

    const merchantsDropdown =
      this.statType === 'merch-ed-overview' && this.state.merchantOptions.length > 0 ? (
        <PartnerDropdown
          gaEvent={{
            category: 'New Merchant Overview',
            label: 'Merchants Dropdown',
          }}
          items={this.state.merchantOptions}
          activeOnly={this.state.params.active_only}
          reconciledOnly={this.state.params.reconciled_only}
          updateMerchantParams={this.updateMerchantParams.bind(this)}
          statType={this.statType}
          staticFields={[]}
          location={this.props.location}
        ></PartnerDropdown>
      ) : null;

    const columnsDropdown =
      this.statType === 'merch-ed-overview' ? (
        <ColumnsDropdown
          gaEvent={{
            category: 'Mega Tables',
            label: `${this.tableName} Column`,
          }}
          items={this.state.columnOptions}
          updateColumns={this.updateColumns.bind(this)}
          resetColumns={this.resetColumns.bind(this)}
          statType={this.statType}
          staticFields={['short_name']}
        ></ColumnsDropdown>
      ) : null;

    const pagination =
      this.statType === 'merch-ed-overview' ? (
        <Pagination
          showPagination={!this.state.showNoResult}
          currentPage={this.state.page}
          totalItems={this.state.totalItems}
          perPage={this.state.perPage}
          eventPrefix={`${this.tableName} Base Overview`}
          updatePage={this.paginationUpdated('page')}
          updatePerPage={this.paginationUpdated('perPage')}
        ></Pagination>
      ) : null;

    const reconciledOnlyToggle =
      this.statType === 'merch-ed-overview' && this.state.params.reconciled_only ? (
        <button
          className="delete-reconciled-only"
          onClick={() =>
            this.updateMerchantParams(
              this.state.merchantOptions,
              this.state.params.active_only,
              false,
            )
          }
        >
          Showing Reconciled Only
        </button>
      ) : null;

    const activeOnlyToggle =
      this.statType === 'merch-ed-overview' && this.state.params.active_only ? (
        <button
          className="delete-active-only"
          onClick={() =>
            this.updateMerchantParams(
              this.state.merchantOptions,
              false,
              this.state.params.reconciled_only,
            )
          }
        >
          Hiding Inactive
        </button>
      ) : null;

    const showTotalsToggle =
      this.statType === 'merch-ed-overview' ? (
        <div
          className="totals-btn"
          onClick={() => this.setState({ showTotals: !this.state.showTotals })}
        >
          <span>{`${this.state.showTotals ? 'Hide' : 'Show'} Dashboard`}</span>
          <span className={`icon ${this.state.showTotals ? 'up' : 'down'}`} />
        </div>
      ) : null;

    const numberRowsLabel =
      this.statType === 'merch-ed-overview' ? (
        <span className="number-rows-label">Displaying {this.state.totalItems} Merchant(s)</span>
      ) : null;

    const noResult = (
      <NoResult
        type={`${this.tableName}`}
        noResult={!this.state.loading && this.state.showNoResult}
      />
    );

    const roiReconContainer =
      this.statType === 'merch-ed-overview' &&
      this.props.flags.roiRecFeatures &&
      this.state.stats &&
      this.state.stats.length ? (
        <div className="roi-recon-picker-container">
          <span
            className={getClass('checkbox', {
              selected: this.state.stats.length === this.state.roiReconSelections.length,
            })}
            onClick={this.updateAllRoiSelections.bind(this)}
          ></span>
          <p className="roi-selected-number">{this.state.roiReconSelections.length} selected</p>
          {this.state.roiReconSelections.length ? <p className="merch-recon-divider">|</p> : null}
          {reconSelectionDropdown}
        </div>
      ) : null;

    const reconAction = this.state.reconAction ? 'Enable' : 'Disable';

    const reconSelectionModal =
      this.statType === 'merch-ed-overview' && this.state.showModal ? (
        <Modal
          options={{
            identifier: `confirm-${reconAction.toLowerCase()}-recon`,
            displayMsg: `${reconAction} ROI Reconciliation?`,
            displayTxt: this.getReconModalDisplayTxt(reconAction),
            save: {
              txt: reconAction,
              callback: () => {
                this.updateMerchRecon();
                this.toggleModal();
              },
            },
            cancel: {
              txt: 'Cancel',
              callback: () => {
                this.updateStatefromReconSelections();
                this.toggleModal();
              },
            },
          }}
        />
      ) : null;

    const noResultFeedback =
      this.statType === 'merch-ed-overview' && !this.state.loading && this.state.showNoResult ? (
        <div className="no-results-feedback">
          {noResult}
          <div className="no-result-sub-message">Try changing or clearing your filters</div>
          <button className="clear-filters-btn" onClick={this.clearFilters.bind(this)}>
            Clear Filters
          </button>
        </div>
      ) : (
        noResult
      );

    return (
      <>
        <div className={`base-overview-container ${this.statType}`}>
          {header}
          <div className="merchant-search">{merchantsDropdown}</div>
          <div className="picker-container">
            <div className="left-pickers">
              {numberRowsLabel}
              {reconciledOnlyToggle}
              {activeOnlyToggle}
            </div>
            <div className="right-pickers">
              {pacingIntervalDropdown}
              {pricingModelDropdown}
              {columnsDropdown}
              <div className="date-picker-container">
                <DatePicker
                  eventPrefix={`${this.tableName} Date Picker`}
                  setDateRange={this.setDateRange.bind(this)}
                  dateRange={this.getDateRange()}
                  allowFutureDates={true}
                />
              </div>
              {showTotalsToggle}
            </div>
          </div>
          {statCards}
          {roiReconContainer}
          {reconSelectionModal}
          <Loader loading={this.state.loading} />
          {noResultFeedback}
          {stats}
        </div>
        {pagination}
      </>
    );
  }
}

export default BaseOverview;
